import { http } from '../plugins/http'
import type { AxiosResponse } from 'axios'

import type PaginatedResponse from '@/types/PaginatedResponse'
import type DataResponse from '@/types/DataResponse'
import type Event from '@/types/Event'
import type Overview from '@/types/Overview'
import type TicketType from '@/types/TicketType'
import type Sector from '@/types/Sector'
import type RevenueRecord from '@/types/RevenueRecord'
import type { AccessControlSector } from '@/types/AccessControl'
import type { EventSeatRow } from '@/types/EventSeats'
import { ImageState } from '@/views/events/partials/form-event-images.vue'
import { SectorConfigState } from '@/views/events/partials/form-event-sectors.vue'

export default class EventService {
  async index(
    page: number,
    filters: string,
    per_page: number | undefined = 10,
  ): Promise<AxiosResponse<PaginatedResponse<Event>>> {
    return await http.get<PaginatedResponse<Event>>(
      `/api/v1/office/events?page=${page}&${filters}&per_page=${per_page}`,
    )
  }

  async show(id: string) {
    return await http.get<DataResponse<Event>>(`/api/v1/office/events/${id}`)
  }

  async store(model: Event, images: ImageState[], sectorConfigs: SectorConfigState[] = []) {
    const form = new FormData()

    images.filter(i => i.file !== undefined || i.dirty).forEach((image, index) => {
      if (image.file !== undefined) {
        form.append(`images[${index}][file]`, image.file as File)
      }

      form.append(`images[${index}][tag]`, image.tag ?? '')
    })

    sectorConfigs.filter(c => c.dirty).forEach((config, index) => {
      if (config.shieldImageFile !== undefined) {
        form.append(`sector_configs[${index}][shield_image]`, config.shieldImageFile)
      }
      form.append(`sector_configs[${index}][sector_id]`, config.sector_id)
      form.append(`sector_configs[${index}][color]`, config.color ?? '')
    })

    form.append('payload', JSON.stringify(model))

    return await http.post<DataResponse<Event>>(
      `/api/v1/office/events`,
      form,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    )
  }

  async update(id: string, model: Event, images: ImageState[], sectorConfigs: SectorConfigState[] = []) {
    delete model.images
    delete model.ticket_types
    delete model.configuration
    delete model.sector_configs

    const form = new FormData()

    images.filter(i => i.file !== undefined || i.dirty).forEach((image, index) => {
      if (image.file !== undefined) {
        form.append(`images[${index}][file]`, image.file as File)
      }

      form.append(`images[${index}][tag]`, image.tag ?? '')
    })

    sectorConfigs.filter(c => c.dirty).forEach((config, index) => {
      if (config.shieldImageFile !== undefined) {
        form.append(`sector_configs[${index}][shield_image]`, config.shieldImageFile)
      }
      form.append(`sector_configs[${index}][sector_id]`, config.sector_id)
      form.append(`sector_configs[${index}][color]`, config.color ?? '')
      form.append(`sector_configs[${index}][description]`, config.description ?? '')
    })

    form.append('payload', JSON.stringify(model))
    form.append('_method', 'PUT')

    return await http.post<DataResponse<Event>>(
      `/api/v1/office/events/${id}`,
      form,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    )
  }

  async destroy(model: Event) {
    return await http.delete(`/api/v1/office/events/${model.id}`)
  }

  async overview(id: string): Promise<AxiosResponse<DataResponse<Overview>>> {
    return await http.get(`/api/v1/office/events/${id}/overview`)
  }

  async types(id: string): Promise<AxiosResponse<TicketType[]>> {
    return (
      await http.get<AxiosResponse<TicketType[]>>(
        `/api/v1/office/events/${id}/types`,
      )
    ).data
  }

  async tickets(id: string): Promise<DataResponse<Sector[]>> {
    const response = await http.get<DataResponse<Sector[]>>(`/api/v1/office/events/${id}/tickets`)
    return response.data
  }

  async sectors(id: string): Promise<AxiosResponse<Sector[]>> {
    return (
      await http.get<AxiosResponse<Sector[]>>(
        `/api/v1/office/events/${id}/sectors`,
      )
    ).data
  }

  async revenue(id: string, filters: string): Promise<AxiosResponse<DataResponse<RevenueRecord[]>>> {
    return await http.get(`/api/v1/office/events/${id}/revenue?${filters}`)
  }

  async accessControl(id: string): Promise<AxiosResponse<DataResponse<AccessControlSector[]>>> {
    return await http.get(`/api/v1/office/events/${id}/access-control`)
  }

  /** The event's Rows, with each seat's status in it */
  async seats(id: string) {
    return await http.get<DataResponse<EventSeatRow[]>>(`/api/v1/office/events/${id}/seats`)
  }

  /** All or nothing: a 409 lists the seats that weren't available, and nothing was blocked */
  async blockSeats(id: string, seats: string[]) {
    return await http.post(`/api/v1/office/events/${id}/seats/blocked`, { seats })
  }

  /** All or nothing: a 409 lists the seats that weren't blocked, and nothing was unblocked */
  async unblockSeats(id: string, seats: string[]) {
    return await http.delete(`/api/v1/office/events/${id}/seats/blocked`, { data: { seats } })
  }
}
